import type { Editor } from '@tiptap/react'
import { useTranslations } from 'next-intl'
import type { FormatAction } from '../../types'
import type { toggleVariants } from '@/components/ui/toggle'
import type { VariantProps } from 'class-variance-authority'
import { LinkEditPopover } from '../link/link-edit-popover'
import { ImageEditDialog } from '../image/image-edit-dialog'
import { ToolbarSection } from '../toolbar-section'
import { ChevronDown } from '@/components/icons'
import { Code, Minus, Plus, Quote } from 'lucide-react'
import { YoutubeEditDialog } from '../youtube/youtube-edit-dialog'

type InsertElementAction = 'codeBlock' | 'blockquote' | 'horizontalRule'
interface InsertElement extends FormatAction {
  value: InsertElementAction
}

const formatActions: InsertElement[] = [
  {
    value: 'codeBlock',
    label: 'code_block',
    icon: <Code className="size-5" />,
    action: (editor) => editor.chain().focus().toggleCodeBlock().run(),
    isActive: (editor) => editor.isActive('codeBlock'),
    canExecute: (editor) =>
      editor.can().chain().focus().toggleCodeBlock().run(),
    shortcuts: ['mod', 'alt', 'C'],
  },
  {
    value: 'blockquote',
    label: 'blockquote',
    icon: <Quote className="size-5" />,
    action: (editor) => editor.chain().focus().toggleBlockquote().run(),
    isActive: (editor) => editor.isActive('blockquote'),
    canExecute: (editor) =>
      editor.can().chain().focus().toggleBlockquote().run(),
    shortcuts: ['mod', 'shift', 'B'],
  },
  {
    value: 'horizontalRule',
    label: 'divider',
    icon: <Minus className="size-5" />,
    action: (editor) => editor.chain().focus().setHorizontalRule().run(),
    isActive: () => false,
    canExecute: (editor) =>
      editor.can().chain().focus().setHorizontalRule().run(),
    shortcuts: ['mod', 'alt', '-'],
  },
]

const createFormatActions = (t: any) => {
  return formatActions.map((d) => ({
    ...d,
    label: t(d.label),
  }))
}

interface SectionSixProps extends VariantProps<typeof toggleVariants> {
  editor: Editor
  activeActions?: InsertElementAction[]
  mainActionCount?: number
  modal?: boolean
}

export const SectionSix: React.FC<SectionSixProps> = ({
  editor,
  activeActions = formatActions.map((action) => action.value),
  mainActionCount = 0,
  size,
  variant,
  modal = false,
}) => {
  const t = useTranslations('tiptap')
  const intlFormatActions = createFormatActions(t)

  return (
    <>
      <LinkEditPopover
        editor={editor}
        size={size}
        variant={variant}
        modal={modal}
      />
      <ImageEditDialog editor={editor} size={size} variant={variant} />
      <YoutubeEditDialog editor={editor} size={size} variant={variant} />
      <ToolbarSection
        editor={editor}
        actions={intlFormatActions}
        activeActions={activeActions}
        mainActionCount={mainActionCount}
        dropdownIcon={
          <>
            <Plus className="size-5" />
            <ChevronDown className="size-5" />
          </>
        }
        dropdownTooltip={t('insert_elements')}
        size={size}
        variant={variant}
      />
    </>
  )
}

SectionSix.displayName = 'SectionSix'

export default SectionSix
